'use client';

import {
	BN,
	BigNum,
	UnifiedSwapClient,
	SwapClientType,
	UnifiedQuoteResponse,
	SwapMode,
	SpotMarketConfig,
	DriftClient,
	TxParams,
} from '@drift-labs/sdk';
import {
	AddressLookupTableAccount,
	Connection,
	PublicKey,
	VersionedTransaction,
	RpcResponseAndContext,
	SimulatedTransactionResponse,
	ParsedInstruction,
	SimulateTransactionConfig,
	TransactionInstruction,
} from '@solana/web3.js';
import { GLOBAL_SETTINGS_CLIENT } from 'src/providers/settingsProvider';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import { SlippageTolerance } from 'src/@types/types';
import { GetTxParamsProps } from '../../transactions/useGetTxParams';
import { createSwapClient } from 'src/utils/swap';

type BuildSwapTxWithRetryLogicBaseProps = {
	accountPubKey: PublicKey;
	inputMarketConfig: SpotMarketConfig;
	outputMarketConfig: SpotMarketConfig;
	inAssociatedTokenAccount?: PublicKey;
	outAssociatedTokenAccount?: PublicKey;
	amount: BN;
	swapMode: SwapMode;
	slippage: SlippageTolerance;
	usdEstimate: number;
	maxRetries: number;
	onlyDirectRoutes?: boolean;
	additionalIxs?: TransactionInstruction[];
	quoteToUse?: UnifiedQuoteResponse;
	forceJupiterClient?: boolean;
	driftClient: DriftClient;
	swapClient: UnifiedSwapClient;
	getTxParams: (props?: GetTxParamsProps) => TxParams;
};

export type BuildSwapTxWithRetryLogicProps =
	BuildSwapTxWithRetryLogicBaseProps &
		(
			| {
					maxAccounts?: number;
					quoteToUse?: never;
			  }
			| {
					quoteToUse: UnifiedQuoteResponse;
					maxAccounts?: never;
			  }
		);

export const buildSwapTxWithRetryLogic = async ({
	accountPubKey,
	inputMarketConfig,
	outputMarketConfig,
	inAssociatedTokenAccount,
	outAssociatedTokenAccount,
	amount,
	swapMode,
	slippage,
	usdEstimate,
	maxAccounts,
	maxRetries,
	onlyDirectRoutes = false,
	additionalIxs = [],
	quoteToUse,
	forceJupiterClient = false,
	driftClient,
	swapClient: initialSwapClient,
	getTxParams,
}: BuildSwapTxWithRetryLogicProps): Promise<{
	tx: VersionedTransaction;
	ixs: TransactionInstruction[];
	lookupTables?: AddressLookupTableAccount[];
	inAmount: BigNum;
	outAmount: BigNum;
	success: boolean;
}> => {
	let swapClient = forceJupiterClient
		? createSwapClient({
				clientType: 'jupiter',
				connection: driftClient?.connection as Connection,
			})
		: initialSwapClient;

	const slippageParams =
		slippage === 'dynamic'
			? {
					autoSlippage: true,
					maxAutoSlippageBps: 300, // 3 pct
					usdEstimate: Math.round(usdEstimate),
				}
			: {
					slippageBps: (slippage as number) * 100,
					autoSlippage: false,
				};

	let maxAccountsToTry = maxAccounts;
	let retries = 0;
	let simulationFailed = true;

	while (simulationFailed && retries <= maxRetries) {
		try {
			// fetch the quote async so it's ready to go
			const quote =
				quoteToUse ??
				(await swapClient?.getQuote({
					inputMint: inputMarketConfig.mint,
					outputMint: outputMarketConfig.mint,
					amount: amount,
					userPublicKey: driftClient?.authority as PublicKey,
					swapMode: swapMode,
					...(swapClient?.isJupiter() && maxAccountsToTry
						? { maxAccounts: maxAccountsToTry }
						: {}),
					onlyDirectRoutes: onlyDirectRoutes || retries > 2,
					...slippageParams,
				}));

			console.log('quote: ', quote);

			const outAmount = new BN(quote?.outAmount ?? 0);
			const inAmount = new BN(quote?.inAmount ?? 0);

			// Use the new unified swap method with UnifiedSwapClient
			const swapIxs = await driftClient?.getSwapIxV2({
				swapClient: swapClient as UnifiedSwapClient,
				outMarketIndex: outputMarketConfig.marketIndex,
				inMarketIndex: inputMarketConfig.marketIndex,
				outAssociatedTokenAccount,
				inAssociatedTokenAccount,
				amount,
				slippageBps: slippageParams.slippageBps,
				swapMode: swapMode,
				onlyDirectRoutes: onlyDirectRoutes || retries > 2,
				reduceOnly: undefined,
				quote: quote,
				userAccountPublicKey: accountPubKey,
			});

			if (!swapIxs) {
				throw new Error('Failed to get swap instructions');
			}

			const instructionsForTx = [...additionalIxs, ...(swapIxs?.ixs ?? [])];

			const tx = (await driftClient?.buildTransaction(
				instructionsForTx,
				getTxParams({
					computeUnits: MAX_COMPUTE_UNITS,
					useSimulatedComputeUnits: false,
				}),
				0,
				swapIxs?.lookupTables
			)) as VersionedTransaction;

			const simulation = (await driftClient?.connection.simulateTransaction(
				tx as VersionedTransaction,
				{
					innerInstructions: true,
				} as SimulateTransactionConfig
			)) as RpcResponseAndContext<
				SimulatedTransactionResponse & {
					innerInstructions: {
						instructions: ParsedInstruction[];
					}[];
				}
			>;

			if (simulation?.value.err) {
				simulationFailed = true;
				maxAccountsToTry = Math.max((maxAccountsToTry ?? 0) - 3, 5);
				retries++;
				// Create a new swap client for retry - use Jupiter if forced, otherwise use the same client type
				const clientType: SwapClientType = forceJupiterClient
					? 'jupiter'
					: (GLOBAL_SETTINGS_CLIENT?.overallSettingsState?.swapClientType ??
						'titan');

				swapClient = createSwapClient({
					clientType,
					connection: driftClient?.connection as Connection,
				});
			} else {
				simulationFailed = false;
				return {
					tx,
					ixs: instructionsForTx,
					lookupTables: swapIxs?.lookupTables,
					inAmount: BigNum.from(inAmount, inputMarketConfig.precisionExp),
					outAmount: BigNum.from(outAmount, outputMarketConfig.precisionExp),
					success: true,
				};
			}
		} catch (e: any) {
			console.log('Failed to simulate:', e);
			simulationFailed = true;
			maxAccountsToTry = Math.max((maxAccountsToTry ?? 0) - 3, 5);
			retries++;
			// Create a new swap client for retry - use Jupiter if forced, otherwise use the same client type
			const clientType: SwapClientType = forceJupiterClient
				? 'jupiter'
				: (GLOBAL_SETTINGS_CLIENT?.overallSettingsState?.swapClientType ??
					'titan');

			swapClient = createSwapClient({
				clientType,
				connection: driftClient?.connection as Connection,
			});
		}
	}

	return {
		tx: null as unknown as VersionedTransaction,
		ixs: [],
		lookupTables: undefined,
		inAmount: null as unknown as BigNum,
		outAmount: null as unknown as BigNum,
		success: false,
	};
};
